import React, { useState, useEffect } from 'react';

const PropertiesPanel = ({ selectedElement, onChange }) => {
    const [config, setConfig] = useState({});
    const [edgeCondition, setEdgeCondition] = useState({});
    const [isJsonMode, setIsJsonMode] = useState(false);
    const [jsonError, setJsonError] = useState(null);

    const isEdge = !!selectedElement?.source;

    useEffect(() => {
        if (selectedElement) {
            if (isEdge) {
                setEdgeCondition(selectedElement.data?.condition || { type: 'always' });
                setConfig({});
            } else {
                let initialConfig = selectedElement.data.config || {};

                // Ensure default values are populated in state if missing in config
                if (selectedElement.type === 'create_reservation') {
                    if (!initialConfig.reservation_date) initialConfig.reservation_date = '2025-12-31';
                    if (!initialConfig.reservation_time) initialConfig.reservation_time = '20:30';
                    if (!initialConfig.reservation_name) initialConfig.reservation_name = '{name}';
                    if (!initialConfig.reservation_number) initialConfig.reservation_number = '{parsed.national_number}';
                    if (!initialConfig.reservation_country_code) initialConfig.reservation_country_code = '{parsed.country_code}';
                    if (!initialConfig.reservation_pax) initialConfig.reservation_pax = '{form_data.data.no_of_people_s}';
                    if (!initialConfig.reservation_business_id) initialConfig.reservation_business_id = '2';
                    if (!initialConfig.output_payload_var) initialConfig.output_payload_var = 'reserv_data';
                }

                setConfig(initialConfig);
                setEdgeCondition({});
            }
            setIsJsonMode(false);
            setJsonError(null);
        }
    }, [selectedElement, isEdge]);

    const handleConfigChange = (key, value) => {
        const newConfig = { ...config, [key]: value };
        setConfig(newConfig);
        onChange(selectedElement.id, { ...selectedElement.data, config: newConfig });
    };

    const handleJsonChange = (jsonString) => {
        try {
            const parsed = JSON.parse(jsonString);
            // In JSON mode, user edits the full config object structure directly
            // For now, let's assume they edit the 'config' part
            const newConfig = parsed.config;
            setConfig(newConfig);
            setJsonError(null);
            onChange(selectedElement.id, { ...selectedElement.data, ...parsed }); // Update entire data if needed, or just config
        } catch (e) {
            setJsonError(e.message);
        }
    };

    const handleEdgeChange = (key, value) => {
        const newCondition = { ...edgeCondition, [key]: value };
        // If switching away from expression, clear the expression string
        if (key === 'type' && value !== 'expression') {
            delete newCondition.expression;
        }
        setEdgeCondition(newCondition);
        onChange(selectedElement.id, {
            ...selectedElement.data,
            condition: newCondition,
            // Update label for visual feedback
            label: value === 'always' ? '' : (value === 'expression' ? '{expr}' : value)
        });
    };

    // --- Edge Render Logic ---
    if (isEdge) {
        return (
            <div className="wf-sidebar-right" style={{
                width: '300px',
                background: '#1e293b',
                borderLeft: '1px solid #334155',
                overflowY: 'auto',
                padding: '15px'
            }}>
                <h3 className="wf-section-title">Edge Properties</h3>
                <div style={{ marginBottom: '15px', color: '#94a3b8', fontSize: '0.8rem' }}>
                    {selectedElement.source} ➝ {selectedElement.target}
                </div>

                <label className="wf-label">Condition Type</label>
                <select
                    className="wf-input"
                    value={edgeCondition.type || 'always'}
                    onChange={(e) => handleEdgeChange('type', e.target.value)}
                >
                    <option value="always">Always</option>
                    <option value="event">Event (Wait Node)</option>
                    <option value="expression">Expression</option>
                </select>

                {edgeCondition.type === 'event' && (
                    <div style={{ marginBottom: '10px' }}>
                        <label className="wf-label">Match ID (Button/Reply ID)</label>
                        <input
                            className="wf-input"
                            value={edgeCondition.match?.id || ''}
                            onChange={(e) => {
                                const newMatch = { ...edgeCondition.match, id: e.target.value };
                                handleEdgeChange('match', newMatch);
                            }}
                            placeholder="e.g. YES_BTN"
                        />
                    </div>
                )}

                {edgeCondition.type === 'expression' && (
                    <div style={{ marginBottom: '10px' }}>
                        <label className="wf-label">Expression</label>
                        <textarea
                            className="wf-input"
                            style={{ fontFamily: 'monospace' }}
                            rows={3}
                            value={edgeCondition.expression || ''}
                            onChange={(e) => handleEdgeChange('expression', e.target.value)}
                            placeholder="{int(form_data.value) > 5}"
                        />
                        <div className="wf-error-text" style={{ color: '#94a3b8', fontSize: '0.7em' }}>
                            Must use valid expression syntax.
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const renderJsonEditor = () => {
        // Construct the object we want to show in JSON mode. 
        // User requested: "json mode should be something like: "config": { ... }"
        // So we wrap config in a structure mirroring the node payload
        const displayObj = {
            config: config
        };

        return (
            <div style={{ marginTop: '10px' }}>
                <label className="wf-label">Raw JSON Config</label>
                <textarea
                    className="wf-input"
                    style={{ fontFamily: 'monospace', minHeight: '300px' }}
                    defaultValue={JSON.stringify(displayObj, null, 2)}
                    onChange={(e) => handleJsonChange(e.target.value)}
                />
                {jsonError && <div style={{ color: '#ef4444', fontSize: '0.8rem' }}>Invalid JSON: {jsonError}</div>}
            </div>
        );
    };

    // ...

    // Helper to render node inputs
    const renderField = (key, label, type = 'text', defaultValue = '') => (
        <div key={key} style={{ marginBottom: '10px' }}>
            <label className="wf-label">{label}</label>
            <input
                type={type}
                className="wf-input"
                value={config[key] !== undefined ? config[key] : defaultValue} // Show actual value or defaultValue if undefined
                onChange={(e) => handleConfigChange(key, e.target.value)}
                placeholder={defaultValue}
            />
        </div>
    );

    const handleVarsChange = (newVars) => {
        const newConfig = { ...config, vars: newVars };
        setConfig(newConfig);
        onChange(selectedElement.id, { ...selectedElement.data, config: newConfig });
    };

    const renderSetVars = () => {
        const vars = config.vars || {};
        const pairs = Object.entries(vars);

        const updateVar = (oldKey, newKey, newVal) => {
            const newVars = { ...vars };
            if (oldKey !== newKey) {
                delete newVars[oldKey];
            }
            newVars[newKey] = newVal;
            handleVarsChange(newVars);
        };

        const removeVar = (key) => {
            const newVars = { ...vars };
            delete newVars[key];
            handleVarsChange(newVars);
        };

        const addVar = () => {
            handleVarsChange({ ...vars, '': '' });
        };

        return (
            <div style={{ marginBottom: '15px' }}>
                <label className="wf-label">Variables (Key : Expression)</label>
                {pairs.map(([key, val], idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                        <input
                            className="wf-input"
                            style={{ flex: 1, fontFamily: 'monospace' }}
                            value={key}
                            placeholder="Key"
                            onChange={(e) => updateVar(key, e.target.value, val)}
                        />
                        <input
                            className="wf-input"
                            style={{ flex: 2, fontFamily: 'monospace' }}
                            value={val}
                            placeholder="Expression"
                            onChange={(e) => updateVar(key, key, e.target.value)}
                        />
                        <button className="wf-btn" style={{ background: '#ef4444', padding: '0 8px' }} onClick={() => removeVar(key)}>×</button>
                    </div>
                ))}
                <button className="wf-btn" style={{ width: '100%', border: '1px dashed #475569' }} onClick={addVar}>+ Add Variable</button>
            </div>
        );
    };


    const renderButtons = () => {
        const buttons = config.buttons || [];

        const addButton = () => {
            const newButtons = [...buttons, { id: '', title: '' }];
            handleConfigChange('buttons', newButtons);
        };

        const updateButton = (index, key, value) => {
            const newButtons = [...buttons];
            newButtons[index] = { ...newButtons[index], [key]: value };
            handleConfigChange('buttons', newButtons);
        };

        const removeButton = (index) => {
            const newButtons = buttons.filter((_, i) => i !== index);
            handleConfigChange('buttons', newButtons);
        };

        return (
            <div style={{ marginBottom: '15px' }}>
                <label className="wf-label">Buttons</label>
                {buttons.map((btn, idx) => (
                    <div key={idx} style={{ padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span className="wf-label">Button {idx + 1}</span>
                            <span style={{ cursor: 'pointer', color: '#ef4444' }} onClick={() => removeButton(idx)}>×</span>
                        </div>
                        <input
                            className="wf-input"
                            value={btn.id}
                            placeholder="ID (e.g. YES_BTN)"
                            onChange={(e) => updateButton(idx, 'id', e.target.value)}
                            style={{ fontSize: '0.8rem', marginBottom: '4px' }}
                        />
                        <input
                            className="wf-input"
                            value={btn.title}
                            placeholder="Title (e.g. Yes please)"
                            onChange={(e) => updateButton(idx, 'title', e.target.value)}
                            style={{ fontSize: '0.8rem' }}
                        />
                    </div>
                ))}
                <button className="wf-btn" style={{ width: '100%', border: '1px dashed #475569' }} onClick={addButton}>+ Add Button</button>
            </div>
        );
    };

    return (
        <div className="wf-sidebar-right" style={{
            width: '300px',
            background: '#1e293b',
            borderLeft: '1px solid #334155',
            overflowY: 'auto',
            padding: '15px'
        }}>
            <h3 className="wf-section-title">Properties</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>ID: {selectedElement.id}</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{selectedElement.data.label}</div>
                </div>
                <button
                    className="wf-btn"
                    style={{ padding: '4px 8px', fontSize: '0.7rem' }}
                    onClick={() => setIsJsonMode(!isJsonMode)}
                >
                    {isJsonMode ? 'Visual Mode' : 'JSON Mode'}
                </button>
            </div>

            {isJsonMode ? renderJsonEditor() : (
                <>
                    {selectedElement.type === 'send_text' && (
                        <div style={{ marginBottom: '10px' }}>
                            <label className="wf-label">Message Text</label>
                            <textarea
                                className="wf-input"
                                rows={5}
                                value={config.text || ''}
                                onChange={(e) => handleConfigChange('text', e.target.value)}
                            />
                        </div>
                    )}

                    {selectedElement.type === 'send_image' && (
                        <>
                            {renderField('media_url', 'Image URL', 'text', 'https://...')}
                            <div style={{ marginBottom: '10px' }}>
                                <label className="wf-label">Caption</label>
                                <textarea
                                    className="wf-input"
                                    rows={4}
                                    value={config.caption || ''}
                                    onChange={(e) => handleConfigChange('caption', e.target.value)}
                                />
                            </div>
                        </>
                    )}

                    {selectedElement.type === 'send_button_message' && (
                        <>
                            {renderField('header_text', 'Header Text')}
                            <div style={{ marginBottom: '10px' }}>
                                <label className="wf-label">Body Text</label>
                                <textarea
                                    className="wf-input"
                                    rows={4}
                                    value={config.body_text || ''}
                                    onChange={(e) => handleConfigChange('body_text', e.target.value)}
                                />
                            </div>
                            {renderButtons()}
                        </>
                    )}

                    {selectedElement.type === 'send_list_message' && (
                        <>
                            {renderField('header_text', 'Header Text')}
                            <div style={{ marginBottom: '10px' }}>
                                <label className="wf-label">Body Text</label>
                                <textarea
                                    className="wf-input"
                                    rows={4}
                                    value={config.body_text || ''}
                                    onChange={(e) => handleConfigChange('body_text', e.target.value)}
                                />
                            </div>
                            {renderField('button_text', 'Menu Button Label')}
                            {/* Simplifying list sections for now to just raw config or simple instruction */}
                            <div style={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic', marginTop: '10px' }}>
                                Interactive List Section builder coming soon.
                            </div>
                        </>
                    )}

                    {selectedElement.type === 'send_form' && (
                        <>
                            {renderField('flow_id', 'Flow ID')}
                            {renderField('flow_token', 'Flow Token')}

                            <label className="wf-label">Header Type</label>
                            <select
                                className="wf-input"
                                value={config.header_type || 'none'}
                                onChange={(e) => handleConfigChange('header_type', e.target.value)}
                            >
                                <option value="none">None</option>
                                <option value="text">Text</option>
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                                <option value="document">Document</option>
                            </select>

                            {config.header_type === 'text' && renderField('header_text', 'Header Text')}

                            {['image', 'video', 'document'].includes(config.header_type) &&
                                renderField('header_media_url', 'Header Media URL')}

                            <div style={{ marginBottom: '10px' }}>
                                <label className="wf-label">Body Text</label>
                                <textarea
                                    className="wf-input"
                                    rows={3}
                                    value={config.body_text || ''}
                                    onChange={(e) => handleConfigChange('body_text', e.target.value)}
                                />
                            </div>
                            {renderField('cta_text', 'CTA Text')}
                        </>
                    )}

                    {/* Wait Configuration for Send Form */}
                    {
                        selectedElement.type === 'send_form' && (
                            <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }}>
                                <div className="wf-section-title" style={{ margin: '0 0 10px', border: 'none' }}>Wait & Process Response</div>
                                <label className="wf-label" style={{ display: 'flex', gap: '5px' }}>
                                    <input
                                        type="checkbox"
                                        checked={config.wait_for_response !== false} // Default true if undefined
                                        onChange={(e) => handleConfigChange('wait_for_response', e.target.checked)}
                                    />
                                    Wait for User Response
                                </label>

                                {config.wait_for_response !== false && (
                                    <>
                                        {renderField('wait_save_as', 'Save Response As (Variable)', 'text', 'form_data')}
                                        {renderField('wait_timeout_ms', 'Timeout (ms)', 'number', '300000')}
                                    </>
                                )}
                            </div>
                        )
                    }

                    {
                        selectedElement.type === 'wait_event' && (
                            <>
                                {renderField('save_as', 'Save Event Data As (Variable)')}
                                {renderField('timeout_ms', 'Timeout (ms)', 'number')}
                            </>
                        )
                    }

                    {selectedElement.type === 'set_vars' && renderSetVars()}

                    {
                        selectedElement.type === 'send_payment_link' && (
                            <>
                                <label className="wf-label">Payment Gateway</label>
                                <select
                                    className="wf-input"
                                    value={config.payment_gateway || ''}
                                    onChange={(e) => handleConfigChange('payment_gateway', e.target.value)}
                                >
                                    <option value="">Select Gateway</option>
                                    <option value="razorpay">Razorpay</option>
                                    <option value="stripe">Stripe</option>
                                </select>
                                <div style={{ marginBottom: '10px' }}>
                                    <label className="wf-label">Body Text</label>
                                    <textarea
                                        className="wf-input"
                                        rows={3}
                                        value={config.body_text || ''}
                                        onChange={(e) => handleConfigChange('body_text', e.target.value)}
                                    />
                                </div>
                                {renderField('catalog_id', 'Catalog ID')}

                                <div style={{ borderTop: '1px solid #334155', margin: '15px 0', paddingTop: '10px' }}>
                                    <div className="wf-section-title" style={{ border: 'none', margin: '0 0 10px' }}>Payment Details</div>
                                    {renderField('currency', 'Currency', 'text', 'INR')}
                                    {renderField('tax_rate', 'Tax Rate', 'number', '0')}
                                    {renderField('total_amount', 'Total Amount (Expression)', 'text', '{1000}')}

                                    <label className="wf-label">Product Items</label>
                                    {(config.product_items || []).map((item, idx) => (
                                        <div key={idx} style={{ padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                                <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Item {idx + 1}</span>
                                                <span
                                                    style={{ cursor: 'pointer', color: '#ef4444' }}
                                                    onClick={() => {
                                                        const newItems = config.product_items.filter((_, i) => i !== idx);
                                                        handleConfigChange('product_items', newItems);
                                                    }}
                                                >×</span>
                                            </div>
                                            {['product_name', 'item_price', 'quantity', 'product_retailer_id'].map(field => (
                                                <input
                                                    key={field}
                                                    className="wf-input"
                                                    style={{ marginBottom: '5px', fontSize: '0.8rem' }}
                                                    placeholder={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    value={item[field] || ''}
                                                    onChange={(e) => {
                                                        const newItems = [...(config.product_items || [])];
                                                        newItems[idx] = { ...newItems[idx], [field]: e.target.value };
                                                        handleConfigChange('product_items', newItems);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    ))}
                                    <button
                                        className="wf-btn"
                                        style={{ width: '100%', border: '1px dashed #475569', fontSize: '0.8rem' }}
                                        onClick={() => {
                                            const newItems = [...(config.product_items || []), { quantity: '1', product_retailer_id: '1' }];
                                            handleConfigChange('product_items', newItems);
                                        }}
                                    >
                                        + Add Item
                                    </button>
                                </div>

                                <label className="wf-label" style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
                                    <input
                                        type="checkbox"
                                        checked={config.pause_after || false}
                                        onChange={(e) => handleConfigChange('pause_after', e.target.checked)}
                                    />
                                    Pause Flow After Sending
                                </label>
                            </>
                        )
                    }

                    {/* Wait & Process Configuration for Send Payment Link */}
                    {
                        selectedElement.type === 'send_payment_link' && (
                            <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }}>
                                <div className="wf-section-title" style={{ margin: '0 0 10px', border: 'none' }}>Wait & Process Payment</div>
                                <label className="wf-label" style={{ display: 'flex', gap: '5px' }}>
                                    <input
                                        type="checkbox"
                                        checked={config.wait_for_payment !== false} // Default true if undefined
                                        onChange={(e) => handleConfigChange('wait_for_payment', e.target.checked)}
                                    />
                                    Wait & Process Payment
                                </label>

                                {config.wait_for_payment !== false && (
                                    <>
                                        {renderField('wait_save_as', 'Save Status As', 'text', 'payment_status')}
                                        {renderField('wait_timeout_ms', 'Timeout (ms)', 'number', '300000')}
                                    </>
                                )}
                            </div>
                        )
                    }

                    {/* Fallback for other types if any */}
                    {
                        (selectedElement.type === 'noop') && (
                            <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                No specific configuration required.
                            </div>
                        )
                    }

                    {
                        (selectedElement.type === 'process_payment') && (
                            <>
                                {renderField('state_input_payload_var', 'State Input Payload Variable', 'text', 'payment_status')}
                            </>
                        )
                    }

                    {
                        (selectedElement.type === 'create_reservation') && (
                            <>
                                <h4 className="wf-section-title" style={{ marginTop: 0 }}>Reservation Details</h4>

                                {renderField('reservation_date', 'Date', 'text', '2025-12-31')}
                                {renderField('reservation_time', 'Time', 'text', '20:30')}
                                {renderField('reservation_name', 'Guest Name', 'text', '{name}')}
                                {renderField('reservation_number', 'Phone Number', 'text', '{parsed.national_number}')}
                                {renderField('reservation_country_code', 'Country Code', 'text', '{parsed.country_code}')}
                                {renderField('reservation_pax', 'No. of People', 'text', '{form_data.data.no_of_people_s}')}
                                {renderField('reservation_business_id', 'Business ID', 'text', '2')}

                                <div style={{ marginBottom: '10px' }}>
                                    <label className="wf-label">Remark</label>
                                    <textarea
                                        className="wf-input"
                                        rows={3}
                                        placeholder="Remark expression..."
                                        value={config.reservation_remark || ''}
                                        onChange={(e) => handleConfigChange('reservation_remark', e.target.value)}
                                    />
                                </div>

                                {renderField('output_payload_var', 'Output Payload Variable', 'text', 'reserv_data')}
                            </>
                        )
                    }

                    {
                        (selectedElement.type === 'start' || selectedElement.type === 'end') && (
                            <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                {selectedElement.type === 'start' ? 'Configure Triggers in Flow Metadata Panel.' : 'Terminates the flow.'}
                            </div>
                        )
                    }
                </>
            )}
        </div>
    );
};

export default PropertiesPanel;
